import _ from 'underscore';
import { PaginationView } from 'pagination-control';
import defaultTemplate from './pagination-template.pug';

const DEFAULT_PAGE_SIZES = [20, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 20;

const patchTemplate = (template, defaults) => options => template(_.defaults(options, defaults));

function normalizeConfig({
  el,
  pageSize,
  pageNumber,
  availablePageSizes,
  template = defaultTemplate,
  enableRandomPage,
  pageNumberLabelText = 'Page:',
  pageSizeText = 'Show rows:',
  totalPageCountText = 'of',
} = {}) {
  const config = {
    el,
    pageSize,
    pageNumber,
    availablePageSizes,
    pageSizeText,
    totalPageCountText,
    enableRandomPage,
  };

  // Normalize the pageSize and availablePageSizes
  if (!_.isFinite(pageSize)) {
    if (_.isFinite(_.first(availablePageSizes))) {
      config.pageSize = _.first(availablePageSizes);
    } else {
      config.availablePageSizes = DEFAULT_PAGE_SIZES;
      config.pageSize = DEFAULT_PAGE_SIZE;
    }
  } else if (!_.isFinite(_.first(availablePageSizes))) {
    config.availablePageSizes = [pageSize];
  }

  // Normalize the pageNumber
  if (!_.isFinite(pageNumber)) {
    config.pageNumber = 0;
  }

  config.template = patchTemplate(template, {
    pageNumberLabelText,
    showPageSizeSelector: config.availablePageSizes.length > 1,
    showTotalPageCount: !!totalPageCountText,
    pageNumberDefaultAriaLabel: 'Page',
  });

  return config;
}

/**
 * @typedef PaginationConfig
 * @type {object}
 * @property {HTMLElement|jQuery|string} el - The element of the pagination control.
 * The pagination control will be rendered in table footer when omitted.
 * @property {number} pageSize - The initial page size.
 * @property {number} pageNumber - The initial page number.
 * @property {number[]} availablePageSizes - The available page sizes user can chooser.
 * @property {function} template - The customized pagination control template.
 * @property {string} pageSizeText - The localized string before page size selector.
 * @property {string} totalPageCountText - The localized string before total page size.
 */

function updateGridViewHandler(gridView, paginationView) {
  const { pageSize, pageNumber, itemCount } = paginationView;
  const skip = pageSize * pageNumber;
  const take = pageSize;

  // Feed the grid view pagination change event to signal plane
  gridView.trigger('signal::pagination.update', { pageSize, pageNumber, itemCount });

  gridView.set({
    query: _.defaults({ skip, take }, gridView.get('query')),
  }, () => {
    paginationView.trigger('change.complete:pagination');
  });
}

function appendViewToGrid(paginationParam, gridView, paginationView) {
  const pagination = paginationParam;
  if (pagination.checkItemCount) {
    // if no item, do not show the paginationView
    if (!(paginationView.itemCount > 0)) {
      return;
    }
  }
  if (!pagination.el && !pagination.appended) {
    const paginationRow = {
      classes: ['noborder', 'pagination-row'],
      view: paginationView,
    };

    if (pagination.position === 'top') {
      gridView.prependHeadRows([paginationRow]);
    } else {
      gridView.appendFootRows([paginationRow]);
    }
    pagination.appended = true;
  }
}

function updatePaginationViewHandler(gridView, paginationView, pagination) {
  const instance = paginationView;
  const { pageSize, pageNumber } = instance;
  const itemCount = gridView.getItemCount();
  const skip = pageSize * pageNumber;

  if (itemCount <= skip && skip > 0) {
    instance.pageNumber = Math.max(0, Math.floor((itemCount - 1) / pageSize));
  }

  instance.itemCount = itemCount;
  appendViewToGrid(pagination, gridView, paginationView);
}

function sortChangedHandler(paginationView, changes) {
  if (_.has(changes, 'sortableHeader')) {
    if (paginationView.pageNumber) {
      // when sorting, force to restore to page number 0
      // eslint-disable-next-line no-param-reassign
      paginationView.pageNumber = 0;
    }
  }
}

export const paginationPlugin = definePlugin => definePlugin(
  'pagination', [
    'config',
    'gridView',
  ],
  ({
    plugins, stateManager, preferenceService, config,
  } = {}, gridView) => {
    const pagination = _.result(plugins, 'pagination', {});
    pagination.appended = false;
    const paginationView = new PaginationView(normalizeConfig(pagination));
    const updatePaginationView =
      _.partial(updatePaginationViewHandler, gridView, paginationView, pagination);
    const updateGridView = _.partial(updateGridViewHandler, gridView, paginationView);

    updateGridView();

    if (pagination.resetPageNumberOnSort) {
      gridView.on('willUpdate', _.partial(sortChangedHandler, paginationView));
    }

    gridView.on('didUpdate', updatePaginationView);
    paginationView.on('change:page-number', updateGridView);
    paginationView.on('change:page-number', () => {
      // eslint-disable-next-line no-underscore-dangle
      gridView.once('didRedraw', () => gridView.getItemCount() &&
        // eslint-disable-next-line no-underscore-dangle
        gridView._tableView.scrollToItem(0, 'bottom', () => {
          // scroll to the top, the virtualized view should not do the topFiller
          // eslint-disable-next-line no-underscore-dangle
          if (gridView._tableView._listView && gridView._tableView._listView.$topFiller) {
            // eslint-disable-next-line no-underscore-dangle
            gridView._tableView._listView.$topFiller.height(0);
          }
        }));
    });
    paginationView.on('change:page-size', (pageSize) => {
      const { disablePreference } = pagination;
      const data = {
        rowsPerPage: pageSize,
      };

      // State manager is being deprecated.
      // It's recommended to use preference service over state manager.
      // So, if preference service is available then don't save to state manager.
      // If you don't have preference service and cannot onboard to it only then use state manager.
      if (_.isObject(preferenceService) && _.isFunction(preferenceService.setAtUserLevel)
        && !preferenceService.isMocked && !disablePreference) {
        let prefix = '';

        if (_.isObject(config) && _.isFunction(config.get)) {
          prefix = config.get('StateManagerKeyPrefix') || '';
        }

        const preferenceData = {
          Name: `${prefix}grid`,
          Data: data,
        };

        preferenceService.setAtUserLevel(preferenceData);
      } else if (_.isObject(stateManager) && _.isFunction(stateManager.setItem)) {
        stateManager.setItem('permanent', 'grid', data);
      }

      // when page size changes, force to restore to page number 0
      paginationView.pageNumber = 0;
      updateGridView();
    });

    // The plugin should render its views, user SHOULD NOT call render() again.
    paginationView.render();

    appendViewToGrid(pagination, gridView, paginationView);

    return paginationView;
  }
);
